import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import ExpandableWrapper from 'Screens/Contextualization/Manage/components/VulnerabilityDetail/components/Tabs/components/GeneralTab/components/ExpandableWrapper';
import useExpandable from 'Hooks/useExpandable';
import {
  selectHostDetailHostnames, selectAssetDetailId,
  selectHostDetailOs, selectHostDetailCreateDate,
  selectToolsUsers, selectToolsParameters, selectToolsHistory,
  selectHostDetailMac,
  selectHostDetail,
  selectHostWorkspace,
  selectHostDetailIp,
  selectHostDetailRisk,
  selectHostDetailOwner,
  selectHostDetailWorkspaceName,
  selectHostDetailServiceSummaries,
  selectHostDetailServices
} from 'store/Contextualization/AssetDetail/selectors';
import TopButtons from './TopButtons';
import CustomList from 'Common/Components/CustomList';
import {
  Wrapper, Row, Field,
  Label, FixField, TextField,
  SummaryList, SummaryItem, SummaryLabel, SummaryValue,
  ServicesTableWrapper, ServicesTable, ServicesHeader, ServicesCell
} from './styled';
import DateFormatter from 'Common/Components/DateFormatter';
import { updateHost } from 'store/Host/actions';
import List from './List';
import { getTools } from 'store/Contextualization/AssetDetail/actions';
import { selectCurrentWorkspace, selectIsReadOnly } from 'store/Faraday/selectors';
import { selectAllowedByRole } from 'Hooks/usePermission/selector';
import { FEATURE_HOSTS, PERMISSION_TYPES } from 'Hooks/usePermission/permissions';
import { updateHostGeneral } from 'store/HostsGeneral/actions';

const ID = 'technicalInfo';
const MIN_MAC_LENGTH = 17;

const TechnicalInfo = ({ assetsGeneral }) => {
  const dispatch = useDispatch();
  const hostDetail = useSelector(selectHostDetail);
  const hostId = useSelector(selectAssetDetailId);
  const hostnames = useSelector(selectHostDetailHostnames);
  const os = useSelector(selectHostDetailOs);
  const creationDate = useSelector(selectHostDetailCreateDate);
  const mac = useSelector(selectHostDetailMac);
  const users = useSelector(selectToolsUsers);
  const parameters = useSelector(selectToolsParameters);
  const toolsHistory = useSelector(selectToolsHistory);
  const [osValue, setOsValue] = useState(os);
  const [macValue, setMacValue] = useState(mac);
  const [isValidMacAddress, setIsValidMacAddress] = useState(true);
  const readOnly = useSelector(selectIsReadOnly);
  const currentWorkspace = useSelector(selectCurrentWorkspace);
  const allowedByRole = useSelector((state) => selectAllowedByRole(state, FEATURE_HOSTS, PERMISSION_TYPES.UPDATE));
  const canUpdate = assetsGeneral ? allowedByRole : !readOnly && allowedByRole;
  const workspace = useSelector(selectHostWorkspace);
  const ip = useSelector(selectHostDetailIp);
  const risk = useSelector(selectHostDetailRisk);
  const owner = useSelector(selectHostDetailOwner);
  const workspaceName = useSelector(selectHostDetailWorkspaceName);
  const workspaceDisplayName = workspaceName || workspace || currentWorkspace;
  const serviceSummaries = useSelector(selectHostDetailServiceSummaries);
  const services = useSelector(selectHostDetailServices);

  const intl = useIntl();
  const title = intl.formatMessage({ id: 'workspace.wsDetail.TechnicalInfo' });

  const isExpanded = useExpandable('general', ID, true, hostId);

  useEffect(() => {
    dispatch(getTools(workspaceDisplayName));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOsValue(hostDetail.os);
    setMacValue(hostDetail.mac);
  }, [hostDetail]);

  const update = (field, value) => assetsGeneral ? dispatch(updateHostGeneral(hostId, { [field]: value }, workspace)) : dispatch(updateHost(hostId, { [field]: value }));

  const uniqueValues = (values = []) => [...new Set(values.filter((value) => value !== null && value !== undefined && value !== ''))];

  const renderListField = (data = []) => (
    data.length > 0 ? <List data={ data } /> : <FixField>—</FixField>
  );

  const renderOwnerValue = () => {
    if (owner) return owner;

    const uniqueUsers = uniqueValues(users);
    return uniqueUsers.length > 0 ? uniqueUsers.join(', ') : '—';
  };

  const getServicePorts = (service = {}) => {
    const ports = uniqueValues(
      Array.isArray(service?.ports)
        ? service.ports
        : [service?.port]
    );

    return ports.length > 0 ? ports.join(', ') : '—';
  };

  const parseServiceSummary = (summary = '') => {
    const match = summary.match(/^\((\d+)\/([^)]+)\)\s+(.+)$/);
    if (match) return { port: match[1], protocol: match[2], name: match[3] };
    return { port: '—', protocol: '—', name: summary };
  };

  const getServiceRows = () => {
    if (services.length > 0) {
      return services.map((service) => ({
        name: service?.name || '—',
        port: getServicePorts(service),
        status: service?.status || '—'
      }));
    }

    return serviceSummaries.map((summary) => {
      const parsed = parseServiceSummary(summary);
      return { name: parsed.name, port: parsed.port, status: '—' };
    });
  };

  const renderServicesTable = () => {
    const serviceRows = getServiceRows();

    return (
      <ServicesTableWrapper>
        <Label>{intl.formatMessage({ id: 'host.hostDetail.services' })}</Label>
        {serviceRows.length > 0
          ? (
          <ServicesTable>
            <thead>
              <tr>
                <ServicesHeader>{intl.formatMessage({ id: 'model.field.service__name' })}</ServicesHeader>
                <ServicesHeader>{intl.formatMessage({ id: 'model.field.service__port' })}</ServicesHeader>
                <ServicesHeader>{intl.formatMessage({ id: 'model.field.services__status' })}</ServicesHeader>
              </tr>
            </thead>
            <tbody>
              {serviceRows.map((service, index) => (
                <tr key={ `asset_detail_service_${service.name}_${service.port}_${index}` }>
                  <ServicesCell>{service.name}</ServicesCell>
                  <ServicesCell>{service.port}</ServicesCell>
                  <ServicesCell>{service.status}</ServicesCell>
                </tr>
              ))}
            </tbody>
          </ServicesTable>
            )
          : <FixField>—</FixField>}
      </ServicesTableWrapper>
    );
  };

  const renderToolsHistory = () => (
    <ServicesTableWrapper>
      <Label>{intl.formatMessage({ id: 'host.hostDetail.toolsHistory' })}</Label>
      {toolsHistory.length > 0
        ? (
        <ServicesTable>
          <thead>
            <tr>
              <ServicesHeader>{intl.formatMessage({ id: 'host.hostDetail.toolsHistory.tool' })}</ServicesHeader>
              <ServicesHeader>{intl.formatMessage({ id: 'host.hostDetail.toolsHistory.commandId' })}</ServicesHeader>
              <ServicesHeader>{intl.formatMessage({ id: 'host.hostDetail.toolsHistory.date' })}</ServicesHeader>
            </tr>
          </thead>
          <tbody>
            {toolsHistory.map((item, index) => (
              <tr key={ `asset_detail_tool_history_${item.commandId}_${index}` }>
                <ServicesCell>{item.tool || '—'}</ServicesCell>
                <ServicesCell>{item.commandId ?? '—'}</ServicesCell>
                <ServicesCell><DateFormatter date={ item.createDate } /></ServicesCell>
              </tr>
            ))}
          </tbody>
        </ServicesTable>
          )
        : <FixField>—</FixField>}
    </ServicesTableWrapper>
  );

  const onRemoveHostname = (indexToRemove) => {
    const newHostnames = hostnames.filter((item, index) => index !== indexToRemove);
    update('hostnames', newHostnames);
  };

  const isMacAddressValid = (str = '') => {
    const value = str.trim();
    if (!value) return false;
    // eslint-disable-next-line prefer-regex-literals
    const regex = new RegExp('^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})|([0-9a-fA-F]{4}\\.[0-9a-fA-F]{4}\\.[0-9a-fA-F]{4})$');
    return regex.test(value);
  };

  const onChangeMac = (value) => {
    setMacValue(value);

    const trimmed = (value || '').trim();
    if (!trimmed) {
      setIsValidMacAddress(true);
      return;
    }

    if (trimmed.length < MIN_MAC_LENGTH) {
      setIsValidMacAddress(true);
      return;
    }
    setIsValidMacAddress(isMacAddressValid(trimmed));
  };

  const updateMacAdress = () => {
    const value = (macValue || '').trim();

    if (!value) return;

    const valid = isMacAddressValid(value);
    setIsValidMacAddress(valid);

    if (valid) {
      update('mac', value);
    }
  };

  return (
    <ExpandableWrapper id={ ID } title={ title } defaultValue={ isExpanded } tab="general" buttons={ <TopButtons assetsGeneral={ assetsGeneral } /> }>
      <Wrapper>
        <SummaryList>
          <SummaryItem>
            <SummaryLabel>{intl.formatMessage({ id: 'host.hostDetail.riskScore' })}</SummaryLabel>
            <SummaryValue>{risk ?? '—'}</SummaryValue>
          </SummaryItem>
          <SummaryItem>
            <SummaryLabel>{intl.formatMessage({ id: 'host.hostDetail.owner' })}</SummaryLabel>
            <SummaryValue>{renderOwnerValue()}</SummaryValue>
          </SummaryItem>
        </SummaryList>
        {renderServicesTable()}
        <Field>
          <Label>{intl.formatMessage({ id: 'host.hostDetail.hostnames' })}</Label>
          <CustomList
            key="HOST_CREATION_HOST_NAMES"
            placeholder="net.hostname.com"
            addItem={ (value) => update('hostnames', [...hostnames, value]) }
            removeItem={ onRemoveHostname }
            listData={ hostnames }
            height="34px"
            responsive
            mtList="11px"
            canUpdate={ canUpdate }
          />
        </Field>
        <Row>
          <Field>
            <Label>{intl.formatMessage({ id: 'host.hostDetail.parameters' })}</Label>
            {renderListField(parameters)}
          </Field>
          <Field>
            <Label>{intl.formatMessage({ id: 'host.hostDetail.creationDate' })}</Label>
            <FixField><DateFormatter date={ creationDate } /></FixField>
          </Field>
        </Row>
        <Row>
          <Field>
            <TextField title={ intl.formatMessage({ id: 'host.hostDetail.os' }) } name="os" placeholder="Unix" value={ osValue } onChange={ (value) => setOsValue(value) } onBlur={ () => update('os', osValue) } disabled={ !canUpdate } />
          </Field>
          <Field>
            <TextField title="MAC Address" name="mac address" placeholder="00:00:5e:00:53:af" value={ macValue } onChange={ onChangeMac } onBlur={ updateMacAdress } error={ !!macValue && !isValidMacAddress } errorMessage={ !!macValue && !isValidMacAddress ? 'Invalid MAC Address' : '' } disabled={ !canUpdate } />
          </Field>
        </Row>
        <Row>
          <Field>
            <Label>{intl.formatMessage({ id: 'host.hostDetail.search' })}</Label>
            <FixField>
              { ip
                ? (
                <a href={ `//shodan.io/search?query=${ip}` } target="_blank" rel="noopener noreferrer">
                  {intl.formatMessage({ id: 'host.hostDetail.searchLink' })}
                </a>
                  )
                : '—'}
            </FixField>
          </Field>
        </Row>
        {renderToolsHistory()}
      </Wrapper>

    </ExpandableWrapper>

  );
};

export default TechnicalInfo;
